import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react'
import { Box, IconButton, Paper, Typography, Tooltip, alpha } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import useFullscreen from '../../shared/hooks/useFullscreen'

interface YouTubePlayer {
    playVideo: () => void
    pauseVideo: () => void
    mute: () => void
    unMute: () => void
    destroy: () => void
    previousVideo: () => void
    nextVideo: () => void
    getPlaylistIndex: () => number
    getPlaylist: () => string[]
}

interface YouTubePlayerEvent {
    data: number
}

interface YouTubePlayerError {
    data: number
}

interface YTNamespace {
    Player: new (elementId: string, config: YouTubePlayerConfig) => YouTubePlayer
    PlayerState: {
        PLAYING: number
    }
}

interface YouTubePlayerConfig {
    videoId: string
    playerVars: {
        /**
         * Controls whether YouTube's native player controls are displayed.
         * 0 = Hide controls (useful for custom controls)
         * 1 = Show controls (default)
         */
        controls: number

        /**
         * Controls which related videos are shown at the end of playback.
         * 0 = Show related videos from the same channel only
         * 1 = Show related videos from any channel (default)
         * Note: You cannot completely disable related videos anymore.
         */
        rel: number

        /**
         * Controls video annotations display.
         * 1 = Show annotations by default
         * 3 = Hide annotations by default
         */
        iv_load_policy: number

        /**
         * Controls YouTube's native fullscreen functionality.
         * 0 = Disable YouTube's fullscreen button (prevents double-click fullscreen)
         * 1 = Enable YouTube's fullscreen button (default)
         * Setting to 0 is crucial for preventing conflicts with custom fullscreen controls.
         */
        fs: number
    }
    events: {
        onReady: () => void
        onStateChange: (event: YouTubePlayerEvent) => void
        onError: (error: YouTubePlayerError) => void
    }
}

declare global {
    interface Window {
        YT?: YTNamespace
        onYouTubeIframeAPIReady?: () => void
    }
}

interface PlayerState {
    isPlaying: boolean
    isMuted: boolean
    isReady: boolean
    currentPlaylistIndex: number
    playlistLength: number
    isHovered: boolean
}

interface CustomVideoPlayerProps {
    videoId?: string
    playlistId?: string
    width?: string | number
    height?: string | number
}

interface CustomVideoPlayerRef {
    pause: () => void
}

const CustomVideoPlayer = forwardRef<CustomVideoPlayerRef, CustomVideoPlayerProps>(
    ({ videoId, playlistId, width = '100%', height = '400px' }, ref) => {
        const [playerState, setPlayerState] = useState<PlayerState>({
            isPlaying: false,
            isMuted: false,
            isReady: false,
            currentPlaylistIndex: 0,
            playlistLength: 0,
            isHovered: false,
        })

        const playerRef = useRef<YouTubePlayer | null>(null)
        const containerRef = useRef<HTMLDivElement>(null)
        const fullscreenTimeoutRef = useRef<NodeJS.Timeout | null>(null)

        const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef)

        const isPlaylistMode = Boolean(playlistId)

        const prepareYouTubeIframeAPI = useCallback((): Promise<void> => {
            if (window.YT?.Player) return Promise.resolve()

            return new Promise<void>((resolve) => {
                if (window.onYouTubeIframeAPIReady) {
                    const originalCallback = window.onYouTubeIframeAPIReady
                    window.onYouTubeIframeAPIReady = () => {
                        originalCallback()
                        resolve()
                    }
                    return
                }

                const script = document.createElement('script')
                script.src = 'https://www.youtube.com/iframe_api'
                script.async = true

                window.onYouTubeIframeAPIReady = resolve
                document.head.appendChild(script)
            })
        }, [])

        const createPlayer = useCallback(async (): Promise<void> => {
            if ((!videoId && !playlistId) || playerRef.current) return

            try {
                await prepareYouTubeIframeAPI()

                if (!window.YT?.Player) {
                    throw new Error('YouTube API not available')
                }

                const config: any = {
                    playerVars: {
                        controls: 0,
                        rel: 0,
                        iv_load_policy: 3,
                        fs: 0,
                    },
                    events: {
                        onReady: () => {
                            setPlayerState((prev) => ({ ...prev, isReady: true }))

                            if (playlistId && playerRef.current) {
                                try {
                                    const playlist = playerRef.current.getPlaylist()
                                    const currentIndex = playerRef.current.getPlaylistIndex()
                                    setPlayerState((prev) => ({
                                        ...prev,
                                        playlistLength: playlist?.length || 0,
                                        currentPlaylistIndex: currentIndex || 0,
                                    }))
                                } catch (error) {
                                    console.log('Could not get playlist info:', error)
                                }
                            }
                        },
                        onStateChange: (event: YouTubePlayerEvent) => {
                            const isPlaying = event.data === window.YT!.PlayerState.PLAYING

                            setPlayerState((prev) => ({ ...prev, isPlaying }))

                            if (playlistId && playerRef.current) {
                                try {
                                    const currentIndex = playerRef.current.getPlaylistIndex()
                                    setPlayerState((prev) => ({
                                        ...prev,
                                        currentPlaylistIndex: currentIndex || 0,
                                    }))
                                } catch (error) {
                                    console.error('YouTube playlist error:', error)
                                }
                            }
                        },
                        onError: (error: YouTubePlayerError) => {
                            console.error('YouTube player error:', error)
                            setPlayerState((prev) => ({ ...prev, isReady: false }))
                        },
                    },
                }

                if (playlistId) {
                    config.playerVars.listType = 'playlist'
                    config.playerVars.list = playlistId
                } else if (videoId) {
                    config.videoId = videoId
                }

                playerRef.current = new window.YT.Player('youtube-player', config)
            } catch (error) {
                console.error('Failed to initialize YouTube player:', error)
            }
        }, [videoId, playlistId, prepareYouTubeIframeAPI])

        const destroyPlayer = useCallback((): void => {
            if (playerRef.current?.destroy) {
                playerRef.current.destroy()
                playerRef.current = null
                setPlayerState({
                    isPlaying: false,
                    isMuted: false,
                    isReady: false,
                    currentPlaylistIndex: 0,
                    playlistLength: 0,
                    isHovered: false,
                })
            }
            if (fullscreenTimeoutRef.current) {
                clearTimeout(fullscreenTimeoutRef.current)
                fullscreenTimeoutRef.current = null
            }
        }, [])

        const togglePlay = useCallback((): void => {
            if (!playerRef.current) return

            try {
                if (playerState.isPlaying) {
                    playerRef.current.pauseVideo()
                } else {
                    playerRef.current.playVideo()
                }
            } catch (error) {
                console.error('Failed to toggle play state:', error)
            }
        }, [playerState.isPlaying])

        const toggleMute = useCallback((): void => {
            if (!playerRef.current) return

            try {
                if (playerState.isMuted) {
                    playerRef.current.unMute()
                } else {
                    playerRef.current.mute()
                }
                setPlayerState((prev) => ({ ...prev, isMuted: !prev.isMuted }))
            } catch (error) {
                console.error('Failed to toggle mute state:', error)
            }
        }, [playerState.isMuted])

        const pause = useCallback((): void => {
            if (playerRef.current && playerState.isPlaying) {
                try {
                    playerRef.current.pauseVideo()
                } catch (error) {
                    console.error('Failed to pause video:', error)
                }
            }
        }, [playerState.isPlaying])

        // Prevent YouTube's native fullscreen on double-click
        const handlePlayerDoubleClick = useCallback(
            (event: React.MouseEvent) => {
                event.preventDefault()
                event.stopPropagation()
                // Instead trigger our custom fullscreen
                toggleFullscreen()
            },
            [toggleFullscreen]
        )

        const playPreviousVideo = useCallback((): void => {
            if (!playerRef.current || !isPlaylistMode) return

            try {
                playerRef.current.previousVideo()
            } catch (error) {
                console.error('Failed to play previous video:', error)
            }
        }, [isPlaylistMode])

        const playNextVideo = useCallback((): void => {
            if (!playerRef.current || !isPlaylistMode) return

            try {
                playerRef.current.nextVideo()
            } catch (error) {
                console.error('Failed to play next video:', error)
            }
        }, [isPlaylistMode])

        const clearFullscreenTimeout = useCallback(() => {
            if (fullscreenTimeoutRef.current) {
                clearTimeout(fullscreenTimeoutRef.current)
                fullscreenTimeoutRef.current = null
            }
        }, [])

        const setFullscreenTimeout = useCallback(() => {
            clearFullscreenTimeout()
            if (isFullscreen && playerState.isPlaying) {
                fullscreenTimeoutRef.current = setTimeout(() => {
                    setPlayerState((prev) => ({ ...prev, isHovered: false }))
                }, 2500)
            }
        }, [isFullscreen, playerState.isPlaying, clearFullscreenTimeout])

        const handleMouseEnter = useCallback(() => {
            setPlayerState((prev) => ({ ...prev, isHovered: true }))
            if (isFullscreen) {
                clearFullscreenTimeout()
            }
        }, [isFullscreen, clearFullscreenTimeout])

        const handleMouseLeave = useCallback(() => {
            if (isFullscreen) {
                setFullscreenTimeout()
            } else {
                setPlayerState((prev) => ({ ...prev, isHovered: false }))
            }
        }, [isFullscreen, setFullscreenTimeout])

        const handleMouseMove = useCallback(() => {
            if (isFullscreen) {
                setPlayerState((prev) => ({ ...prev, isHovered: true }))
                setFullscreenTimeout()
            }
        }, [isFullscreen, setFullscreenTimeout])

        useImperativeHandle(ref, () => ({ pause }), [pause])

        useEffect(() => {
            createPlayer()
            return destroyPlayer
        }, [createPlayer, destroyPlayer])

        useEffect(() => {
            if (!isFullscreen) {
                clearFullscreenTimeout()
                setPlayerState((prev) => ({ ...prev, isHovered: true }))
            }
        }, [isFullscreen, clearFullscreenTimeout])

        const { isPlaying, isMuted, isReady, currentPlaylistIndex, playlistLength, isHovered } = playerState

        const showControls = !isPlaying || isHovered

        return (
            <Paper
                ref={containerRef}
                elevation={3}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                sx={{
                    position: 'relative',
                    width,
                    height,
                    overflow: 'hidden',
                    borderRadius: 2,
                    backgroundColor: '#000',
                }}
            >
                <Box
                    id="youtube-player"
                    onDoubleClick={handlePlayerDoubleClick}
                    sx={{
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                    }}
                />

                {isReady && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 5,
                            left: 12,
                            height: '47px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            borderRadius: '24px',
                            padding: 1,
                            zIndex: 99999,
                            transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                            pointerEvents: showControls ? 'auto' : 'none',
                            opacity: showControls ? 1 : 0,
                            transform: showControls ? 'translateY(0)' : 'translateY(10px)',
                        }}
                    >
                        {isPlaylistMode && playlistLength > 0 && (
                            <>
                                <Typography
                                    variant="14x600"
                                    mb={'2px'}
                                    sx={{
                                        color: 'white',
                                        minWidth: '40px',
                                        textAlign: 'center',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {currentPlaylistIndex + 1} / {playlistLength}
                                </Typography>
                                <Box
                                    sx={{
                                        width: '1px',
                                        height: '20px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        mx: 0.5,
                                    }}
                                />
                            </>
                        )}

                        {isPlaylistMode && (
                            <Tooltip title="Previous video" arrow>
                                <span>
                                    <IconButton
                                        onClick={playPreviousVideo}
                                        disabled={currentPlaylistIndex === 0}
                                        sx={{
                                            color: currentPlaylistIndex === 0 ? 'rgba(255, 255, 255, 0.6)' : 'white',
                                            '&:hover': {
                                                backgroundColor:
                                                    currentPlaylistIndex === 0
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(255, 255, 255, 0.1)',
                                            },
                                            '&:disabled': {
                                                color: 'rgba(255, 255, 255, 0.6)',
                                            },
                                        }}
                                        size="small"
                                    >
                                        <SkipPreviousIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}

                        <Tooltip title={isPlaying ? 'Pause' : 'Play'} arrow>
                            <IconButton
                                onClick={togglePlay}
                                sx={{
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                }}
                                size="small"
                            >
                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                        </Tooltip>

                        {isPlaylistMode && (
                            <Tooltip title="Next video" arrow>
                                <span>
                                    <IconButton
                                        onClick={playNextVideo}
                                        disabled={currentPlaylistIndex >= playlistLength - 1}
                                        sx={{
                                            color:
                                                currentPlaylistIndex >= playlistLength - 1
                                                    ? 'rgba(255, 255, 255, 0.6)'
                                                    : 'white',
                                            '&:hover': {
                                                backgroundColor:
                                                    currentPlaylistIndex >= playlistLength - 1
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(255, 255, 255, 0.1)',
                                            },
                                            '&:disabled': {
                                                color: 'rgba(255, 255, 255, 0.6)',
                                            },
                                        }}
                                        size="small"
                                    >
                                        <SkipNextIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}

                        <Tooltip title={isMuted ? 'Unmute' : 'Mute'} arrow>
                            <IconButton
                                onClick={toggleMute}
                                sx={{
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                }}
                                size="small"
                            >
                                {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} arrow>
                            <IconButton
                                onClick={toggleFullscreen}
                                sx={{
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                }}
                                size="small"
                            >
                                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Paper>
        )
    }
)

CustomVideoPlayer.displayName = 'CustomVideoPlayer'

export default CustomVideoPlayer
